import * as React from 'react';
import { DropdownButton, Dropdown } from 'react-bootstrap';
import Toggle from 'react-toggle';
import Icon from '../common/Icon';
import {
    bulkUpdateInstalls,
    getReportPreview,
    getReportPreviewInline,
    setEnableInstallBatchMode,
    startBulkUpdateInstallProduct
} from '../../actions/manageInventoryActions';
import {
    markSelectedAs,
    passSelected,
    startBulkCannotCompleteSelected
} from '../../actions/measurementPointResultsActions';
import filter from 'lodash/filter';
import { toastr } from 'react-redux-toastr';
import { RouteComponentProps } from 'react-router';
import { completeJob, unlinkWorkOrders } from '../../actions/manageJobActions';
import { toggleJobCommentsModal } from '../../actions/manageJobCommentsActions';
import { constants } from '../../constants/constants';
import {
    IWorkOrder,
    Ifacility,
    IinstallBasePart,
    IinstallBasePopulated,
    Ijob,
    Iuser
} from '../../models';
import {
    measurementPointListTypeEnum,
    measurementPointResultStatusTypesEnum,
    workOrderStatusEnum,
    jobStatusEnum,
    UserTypeEnum,
    jobTypesIdEnum
} from '../../models-enums';
import { securityFunctions } from '../../constants/securityFunctions';
import { isApproverReviewing } from '../../utils/reviewFlow';
import { defaultIncludeNotTestedAssets, isPopupBlocked } from './jobFinalCheckUtil';
import { useDispatch } from 'react-redux';
import { TFunction } from 'react-i18next';
import { toggleModalVirtualSapWoParts } from '../../actions/manageInventoryActions';

interface Iprops extends RouteComponentProps<any> {
    t: TFunction;
    facility: Ifacility;
    selectedJob: Ijob;
    isJobReadOnly: boolean;
    toggleSearchNewProductsModal: () => void;
    toggleEditJobModal: () => void;
    completeJob: typeof completeJob;
    passSelected: typeof passSelected;
    startBulkCannotCompleteSelected: typeof startBulkCannotCompleteSelected;
    showCompleteJobButton: boolean;
    showReportPreviewButton: boolean;
    installBasesPopulated: IinstallBasePopulated[];
    colorButton: string;
    setEnableInstallBatchMode: typeof setEnableInstallBatchMode;
    toggleJobSignatureModal: () => void;
    toggleModalSignaturePad: () => void;
    toggleCommissioningDataFormModal: () => void;
    toggleEditInstallModal: () => void;
    startBulkUpdateInstallProduct: typeof startBulkUpdateInstallProduct;
    toggleIsJobClosingWithSignature: () => void;
    hideJobClosingWithSignature: () => void;
    toggleIsCollectingSignatures: () => void;
    selection: string[];
    bulkUpdateInstalls: typeof bulkUpdateInstalls;
    toggleAddHoursModal: () => void;
    toggleEditJobDefaultsModal: () => void;
    isAuditJob: boolean;
    isCommissioningJob: boolean;
    isMaintenanceJob: boolean;
    isRepairJob: boolean;
    isVerificationJob: boolean;
    isWarrantyJob: boolean;
    isServicePlanJob: boolean;
    isAgsJob: boolean;
    isAnnualJob: boolean;
    isWorkOrder: boolean;
    showEditJobDefaultsButton: boolean;
    resetSelection: () => void;
    getReportPreview: typeof getReportPreview;
    getReportPreviewInline: typeof getReportPreviewInline;
    online: boolean;
    onMenuToggle?: (isOpen: boolean) => void;
    unlinkWorkOrders: typeof unlinkWorkOrders;
    toggleModalAddFSE: () => void;
    toggleModalEditFacilityContacts: () => void;
    workOrders: { [key: string]: IWorkOrder };
    installBaseParts: { [key: string]: IinstallBasePart };
    user: Iuser;
    hideVirtualDecommissioned?: boolean;
    onHideVirtualChange?: (checked: boolean) => void;
}

// job data and job notes open as read only views for the approver of a completed job (MMG-4480)
const readOnlyEventKeys = [5, 16];

export const InventoryActionButton = (props: Iprops) => {
    const {
        t,
        facility,
        toggleSearchNewProductsModal,
        toggleEditJobDefaultsModal,
        toggleEditJobModal,
        history,
        toggleAddHoursModal,
        showCompleteJobButton,
        showReportPreviewButton,
        showEditJobDefaultsButton,
        installBasesPopulated,
        toggleEditInstallModal,
        toggleCommissioningDataFormModal,
        toggleJobSignatureModal,
        toggleModalSignaturePad,
        selection,
        isCommissioningJob,
        isMaintenanceJob,
        isRepairJob,
        isVerificationJob,
        isWarrantyJob,
        isAgsJob,
        isAnnualJob,
        isServicePlanJob,
        isAuditJob,
        resetSelection,
        onMenuToggle,
        toggleModalAddFSE,
        toggleModalEditFacilityContacts,
        user
    } = props;

    const dispatch = useDispatch();

    const isFSEApprovalFlow = user?.type === UserTypeEnum.FSEApprovalFlow;
    const isInspectionJob = props.selectedJob?.jobTypeID === jobTypesIdEnum.inspection;
    const requiresReview = isFSEApprovalFlow && isInspectionJob;

    const checkSelectionLimit = (limit = constants.bulkSelectionLimit) => {
        if (selection.length >= limit) {
            toastr.warning(
                t('toastMessage:warning'),
                t('toastMessage:bulkSelectionLimit', {
                    limit
                }),
                constants.toastrWarning
            );
            return Promise.reject(false);
        } else if (selection.length === 0) {
            toastr.warning(
                t('toastMessage:warning'),
                t('toastMessage:bulkSelectionMissing', {
                    limit
                }),
                constants.toastrWarning
            );
            return Promise.reject(false);
        } else {
            return Promise.resolve(true);
        }
    };

    const selectionToInstalls = (
        selectionIds: string[],
        installBases: IinstallBasePopulated[]
    ): IinstallBasePopulated[] => {
        return selection
            .map(item => item.split('select-')[1])
            .map(id => installBases.find(i => i.id === id))
            .filter(i => i !== undefined) as IinstallBasePopulated[];
    };

    const enableUnlinkWOS = (selectionIds: string[], installBases: IinstallBasePopulated[]): boolean => {
        const installs = selectionToInstalls(selectionIds, installBases);
        // .every() on an empty selection is vacuously true; require at least one selected install
        // MMG-4194: any work order on this job qualifies, regardless of source
        return (
            installs.length > 0 &&
            installs.every(installBase => (installBase.workOrders || []).length > 0)
        );
    };

    const doesSelectionIncludeValidAssets = () => {
        const selectedInstallBases = selectionToInstalls(selection, installBasesPopulated);

        return new Promise((resolve, reject) => {
            const hasInvalidStatus = selectedInstallBases.some(
                install =>
                    install.measurementPointListResultStatus ===
                        measurementPointResultStatusTypesEnum.resultStatusDecommissioned ||
                    install.measurementPointListResultStatus ===
                        measurementPointResultStatusTypesEnum.resultStatusNotUnderContract
            );

            if (hasInvalidStatus) {
                toastr.error(
                    t('toastMessage:error'),
                    t('manageInventory:bulkActionAssetValidation'),
                    constants.toastrError
                );
                return reject(false);
            } else {
                return resolve(true);
            }
        });
    };

    const checkSelectionPassLimit = () => checkSelectionLimit(constants.bulkSelectionPassLimit);

    const unlinkWorkOrdersCheck = () => {
        let showWarning = false;

        // installBase.workOrders is scoped to the current job; the global workOrdersByID store is not
        const selectedWorkOrders = selectionToInstalls(selection, installBasesPopulated).reduce<IWorkOrder[]>(
            (wos, installBase) => wos.concat(installBase.workOrders || []),
            []
        );

        // MMG-4294: a blank status (0) only comes from work orders born in the add-asset flow - never-completed, safe to unlink
        const anyCompletedWorkOrders = selectedWorkOrders.some(
            wo => Boolean(wo?.status) && wo.status !== workOrderStatusEnum.new
        );
        if (anyCompletedWorkOrders) {
            toastr.error(t('toastMessage:error'), 'Completed work orders cannot be unlinked.', constants.toastrError);
            return;
        }

        // Check if the job has hours
        if (props.selectedJob.jobHours && props.selectedJob.jobHours.length > 0) {
            showWarning = true;
        }
        // Have any work order parts been added to this job?
        const partsUsed = filter(props.installBaseParts, x => x.jobID === props.selectedJob.id);
        if (partsUsed.length > 0) {
            const WOPartsUsed = partsUsed.filter(x => x.workOrderPartQuantity && x.workOrderPartQuantity > 0);
            if (WOPartsUsed.length > 0) {
                let selectedWorkOrdersHaveWOPartsUsed = false;
                // Check if the used work order parts belong to any of the selected work orders that are trying to be unlinked
                WOPartsUsed.map((x: IinstallBasePart) => {
                    if (selectedWorkOrders.some(wo => wo?.installBaseID === x.installBaseID)) {
                        selectedWorkOrdersHaveWOPartsUsed = true;
                    }
                });

                if (selectedWorkOrdersHaveWOPartsUsed) {
                    showWarning = true;
                }
            }
        }

        if (showWarning) {
            const toastrConfirmOptions = {
                onOk: () => {
                    props.unlinkWorkOrders(t);
                },
                onCancel: () => null,
                okText: 'Go Ahead',
                cancelText: 'Cancel'
            };
            toastr.confirm(props.t('nsJob:removeSAPWorkOrderWarning'), toastrConfirmOptions);
        } else {
            props.unlinkWorkOrders(t);
        }
    };

    const getAddJobDataURL = (): string => {
        if (isAgsJob) {
            return `/simple_list?type=${measurementPointListTypeEnum.agsRebalancing}`;
        } else if (isCommissioningJob) {
            return `/simple_list?type=${measurementPointListTypeEnum.commissioning}`;
        } else if (isMaintenanceJob || isRepairJob) {
            return `/simple_list?type=${measurementPointListTypeEnum.touchpoint}`;
        } else if (isVerificationJob) {
            return `/simple_list?type=${measurementPointListTypeEnum.verification}`;
        } else if (isAnnualJob) {
            return `/simple_list?type=${measurementPointListTypeEnum.annual}`;
        } else if (isAuditJob) {
            return `/simple_list?type=${measurementPointListTypeEnum.audit}`;
        }

        return `/simple_list`;
    };

    const handleOnSelect = async (eventKey: any) => {
        if (props.isJobReadOnly && !readOnlyEventKeys.includes(Number(eventKey))) {
            return;
        }
        // the selections are guids prefixed with select-
        // need to map and move the select- prefix to the end of the guid
        const selectionIds = selection.map(item => {
            return item.split('select-')[1];
        });
        switch (Number(eventKey)) {
            case 1: // new install
                toggleSearchNewProductsModal();
                props.setEnableInstallBatchMode(false);
                break;
            case 3: // add job
                toggleEditJobModal();
                break;
            case 4:
                if (props.selectedJob.status === jobStatusEnum.reopened) {
                    toastr.warning(
                        'Note',
                        'NOTE: Any hours added to a "re-opened" job will only be reflected on the printed report. It will NOT adjust your hours in SAP. Please contact your Planner / Service Coordinator to adjust hours in SAP.',
                        constants.toastrWarning
                    );
                }
                toggleAddHoursModal();
                break;
            case 5:
                dispatch(toggleJobCommentsModal());
                break;
            case 50:
                history.push(`/simple_list?type=${measurementPointListTypeEnum.purity}`);
                break;
            case 51:
                history.push(`/simple_list?type=${measurementPointListTypeEnum.verificationChecklist}`);
                break;
            case 52:
                toggleEditJobDefaultsModal();
                break;
            case 6: // complete job
                props.toggleIsJobClosingWithSignature();
                props.completeJob(t, installBasesPopulated, isCommissioningJob, facility?.countryID, props.history);
                break;
            // client asked to remove bulk close work orders April 15 2020
            case 7: // Get Report Preview — in-browser, consistent with the Final Check modal
                {
                    // Progress shows in the header CubeLoader: getReportPreviewInline
                    // dispatches beginAjaxCall/endAjaxCall, which drive selectIsLoading.
                    // The tab is opened only once the PDF is ready (MMG-4296/4297), which
                    // puts it outside the click gesture — tell the FSE if it gets blocked
                    // rather than leaving them with nothing.
                    // MMG-4262: no toggle on this menu, so fall back to the same default the
                    // Final Check screen starts from — otherwise this preview shows assets the
                    // closed job's report will exclude.
                    const result = props.getReportPreviewInline(
                        props.selectedJob.id,
                        defaultIncludeNotTestedAssets(props.selectedJob.jobTypeID)
                    ) as any;
                    Promise.resolve(result)
                        .then((objectUrl: string | undefined) => {
                            if (objectUrl && isPopupBlocked(window.open(objectUrl, '_blank'))) {
                                toastr.warning(
                                    'Report Preview',
                                    String(t('nsJob:reportPreviewBlockedHint')),
                                    constants.toastrWarning
                                );
                            }
                        })
                        .catch((error: any) => {
                            constants.handleError(error, 'preview report');
                            console.error('[getReportPreviewInline]:', error);
                        });
                }
                break;
            case 8: // pass
                doesSelectionIncludeValidAssets()
                    .then(() => checkSelectionPassLimit())
                    .then(() => props.passSelected(t))
                    .catch(() => {
                        // Error handling for pass selected operation
                    });
                break;
            case 9: // cannot complete all selected
                doesSelectionIncludeValidAssets()
                    .then(() => checkSelectionPassLimit())
                    .then(() => props.startBulkCannotCompleteSelected(installBasesPopulated, t))
                    .catch(() => {
                        // Error handling for cannot complete all selected operation
                    });
                break;
            case 10: // edit
                doesSelectionIncludeValidAssets()
                    .then(() => checkSelectionLimit())
                    .then(() => {
                        toggleEditInstallModal();
                        props.setEnableInstallBatchMode(true);
                    })
                    .catch(() => {
                        // Error handling for edit operation
                    });
                break;
            case 11:
                {
                    // delete
                    // Check if SAP assets, if so, display a different message

                    const sapInstallBasesToUpdate = filter(installBasesPopulated, install => {
                        if (selectionIds.indexOf(install.id) === -1) {
                            return false;
                        }
                        if (install.isDeleted === true) {
                            return false;
                        }
                        return !!install.sapEquipmentNumber;
                    });
                    if (sapInstallBasesToUpdate.length > 0) {
                        toastr.warning(
                            t('toastMessage:warning'),
                            t('toastMessage:bulkDeleteSAPAssetsWarning'),
                            constants.toastrWarning
                        );
                        return;
                    }

                    doesSelectionIncludeValidAssets()
                        .then(() => checkSelectionLimit(constants.bulkDeleteSelectionLimit))
                        .then(() => {
                            const toastConfirmAgainOptions = {
                                onOk: () => {
                                    props.bulkUpdateInstalls({ isDeleted: true });
                                },
                                onCancel: () => null,
                                okText: t('buttonBulkDeleteInstalls'),
                                cancelText: t('common:cancel')
                            };

                            const toastrConfirmOptions = {
                                onOk: () => {
                                    // Use setTimeout to let the first dialog close before showing the second
                                    setTimeout(() => {
                                        toastr.confirm(t('installDeleteSecondWarning'), toastConfirmAgainOptions);
                                    }, 100);
                                },
                                onCancel: () => null,
                                okText: t('buttonBulkDeleteInstalls'),
                                cancelText: t('common:cancel')
                            };

                            toastr
                                .confirm(
                                    t('toastMessage:confirmBulkDeleteInstalls', {
                                        count: selection.length
                                    }),
                                    toastrConfirmOptions
                                )
                                .then(() => {
                                    // Success case
                                })
                                .catch((err: any) => {
                                    console.error('Asset deletion failed:', {
                                        err,
                                        selectionLength: selection.length,
                                        selectionIds,
                                        selectedJob: props.selectedJob?.id,
                                        facility: facility?.id
                                    });

                                    // Show user-friendly error message
                                    toastr.error(
                                        t('toastMessage:error'),
                                        t('toastMessage:operationFailed') ||
                                            'The deletion operation failed. Please try again.',
                                        constants.toastrError
                                    );
                                });
                        })
                        .catch(() => {
                            // Error handling for asset deletion
                        });
                }
                break;
            case 12: // change product
                doesSelectionIncludeValidAssets()
                    .then(() => checkSelectionLimit())
                    .then(() => props.startBulkUpdateInstallProduct(t))
                    .catch(() => {
                        // Error handling for change product operation
                    });
                break;
            case 13: // reset selection
                resetSelection();
                break;
            case 14: // add commissioning data
                toggleCommissioningDataFormModal();
                break;
            case 15: // add job signatures
                props.toggleIsCollectingSignatures();
                // Ensure job closing flag is cleared when collecting signatures independently
                // This prevents the Final Check modal from appearing when saving signatures
                // if the flag was previously set from a job completion attempt
                props.hideJobClosingWithSignature();
                // Only show the approver signing screen when the job is actually
                // in review and the user is its approver (see utils/reviewFlow).
                if (isApproverReviewing(props.selectedJob, user)) {
                    dispatch({ type: 'SHOW_MODAL_APPROVER_SIGNING' });
                }
                if (isCommissioningJob && facility?.countryID === constants.ukCountryID) {
                    toggleJobSignatureModal();
                } else {
                    toggleModalSignaturePad();
                }
                break;
            case 16: // add job data
                history.push(getAddJobDataURL());
                break;
            case 17: // unlink work orders
                checkSelectionLimit()
                    .then(() => {
                        unlinkWorkOrdersCheck();
                    })
                    .catch(() => {
                        // Error handling for unlink work orders operation
                    });
                break;
            case 18: // Add Additional FSE
                toggleModalAddFSE();
                break;
            case 19: // Edit Facility Contacts
                toggleModalEditFacilityContacts();
                break;
            case 20: // Decommission Asset(s)
                checkSelectionLimit().then(() => {
                    dispatch(markSelectedAs(measurementPointResultStatusTypesEnum.resultStatusDecommissioned, t));
                });

                break;
            case 22: // Virtual SAP WO parts
                dispatch(toggleModalVirtualSapWoParts());
                break;
            case 21: // Mark "not under contract"
                checkSelectionLimit().then(() => {
                    dispatch(markSelectedAs(measurementPointResultStatusTypesEnum.resultStatusNotUnderContract, t));
                });
                break;
            default:
                break;
        }
    };

    let anyVirtualIBs = false;

    if (
        props.selection &&
        props.selection.length > 0 &&
        props.installBasesPopulated &&
        props.installBasesPopulated.length > 0
    ) {
        props.selection.forEach(selectionItem => {
            const installBase = props.installBasesPopulated.find(ib => ib.id === selectionItem.split('select-')[1]);
            if (installBase && installBase.isVirtual) {
                anyVirtualIBs = true;
            }
        });
    }

    const canDeleteAssets = () => {
        // First check: If no assets are selected, deletion is not allowed
        const emptySelection = selection.length === 0;
        if (emptySelection) {
            return false;
        }

        // Check if user has permission to delete SAP assets
        const hasDeleteSF = constants.hasSecurityFunction(user, securityFunctions.DeleteSAPAsset.id);

        // Check if ALL selected assets have SAP equipment numbers
        const allAssetsHaveSAP = selection.every(item => {
            const installBase = props.installBasesPopulated.find(ib => ib.id === item.split('select-')[1]);
            return installBase && installBase.sapEquipmentNumber;
        });

        // Check if ALL selected assets have NO SAP equipment numbers
        const allAssetsNonSAP = selection.every(item => {
            const installBase = props.installBasesPopulated.find(ib => ib.id === item.split('select-')[1]);
            return installBase && !installBase.sapEquipmentNumber;
        });

        // Case 1: If all selected assets are non-SAP, deletion is allowed
        if (allAssetsNonSAP) {
            return true;
        }

        // Case 2: If all assets are SAP assets
        if (allAssetsHaveSAP) {
            // Only allow deletion if user has SAP delete permission
            if (hasDeleteSF) {
                return true;
            }
            return false;
        }

        // Implicit Case 3: Mixed SAP and non-SAP assets
        // Currently returns undefined, should probably return false
        return false; // Add this to handle mixed case explicitly
    };

    const renderToggleTitle = () => (
        <Icon name="ellipsis" />
    );

    const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        if (props.onHideVirtualChange) {
            props.onHideVirtualChange(e.target.checked);
        }
    };

    return (
        <div className="inventory-action-ellipsis">
            <DropdownButton
                drop="down"
                variant="default"
                title={renderToggleTitle()}
                id="inventory-action-ellipsis-button"
                onSelect={handleOnSelect}
                {...(onMenuToggle && { onToggle: onMenuToggle })}
            >
                {props.onHideVirtualChange && (
                    <>
                        <div className="hide-virtual-toggle-dropdown" onClick={(e) => e.stopPropagation()}>
                            <label className="hide-virtual-toggle-label-container">
                                <Toggle
                                    icons={false}
                                    className="beacon-toggle"
                                    checked={props.hideVirtualDecommissioned || false}
                                    onChange={handleToggleChange}
                                />
                                <span className="hide-virtual-toggle-label">
                                    Hide virtual/decommissioned assets
                                </span>
                            </label>
                        </div>
                        <Dropdown.Divider />
                    </>
                )}
                <div className="action-section-header">
                    <p>{String(t('mobile:jobActions'))}</p>
                </div>
                <Dropdown.Item eventKey={16}>{String(t('mobile:addJobData'))}</Dropdown.Item>
                {!props.isJobReadOnly && (
                    <>
                        {/* Disabling since currently is not being used but prefer to not remove. Remove once all stakeholders are aware and are on board with this */}
                        <Dropdown.Item eventKey={3} disabled>
                            {String(t('mobile:addJobButton'))}
                        </Dropdown.Item>
                        <Dropdown.Item eventKey={4}>{String(t('mobile:addHoursButton'))}</Dropdown.Item>
                    </>
                )}
                <Dropdown.Item eventKey={5}>{String(t('mobile:addJobNotes'))}</Dropdown.Item>
                {!props.isJobReadOnly && (
                    <>
                        <Dropdown.Item eventKey={18}>{String(t('mobile:addAdditionalFSE'))}</Dropdown.Item>
                        {isCommissioningJob && (
                            <Dropdown.Item eventKey={14}>{String(t('mobile:addCommissioningData'))}</Dropdown.Item>
                        )}
                        <Dropdown.Item eventKey={15}>{String(t('mobile:collectSignatures'))}</Dropdown.Item>
                        {/* Something about the React.Fragment prevents lumping these two together with our switchcase */}
                        {isVerificationJob && <Dropdown.Item eventKey={50}>{String(t('mobile:purity'))}</Dropdown.Item>}
                        {isVerificationJob && (
                            <Dropdown.Item eventKey={51}>{String(t('mobile:verificationChecklist'))}</Dropdown.Item>
                        )}
                        {showEditJobDefaultsButton && (
                            <Dropdown.Item eventKey={52}>{String(t('manageInventory:editJobDefaultsModal'))}</Dropdown.Item>
                        )}
                        {constants.hasSecurityFunction(user, securityFunctions.FSEFacilityContactEdit.id) && (
                            <Dropdown.Item eventKey={19}>{String(t('manageInventory:editFacilitycontacts'))}</Dropdown.Item>
                        )}
                        {showCompleteJobButton && (
                            <Dropdown.Item eventKey={6}>
                                {requiresReview
                                    ? String(t('nsJob:sendForReview'))
                                    : String(t('mobile:completeJobButton'))}
                            </Dropdown.Item>
                        )}
                        {showReportPreviewButton && props.online && (
                            <Dropdown.Item eventKey={7}>{String(t('mobile:getReportPreview'))}</Dropdown.Item>
                        )}

                        <Dropdown.Divider />
                        <div className="action-section-header">
                            <p>{String(t('mobile:individualActions'))}</p>
                        </div>
                        <Dropdown.Item eventKey={1}>{String(t('manageInventory:newInstall'))}</Dropdown.Item>
                        <Dropdown.Item eventKey={22}>{String(t('manageInventory:virtualSapWoParts'))}</Dropdown.Item>

                        <Dropdown.Divider />
                        <div className="action-section-header">
                            <p>{String(t('mobile:batchActions'))}</p>
                        </div>
                        <Dropdown.Item
                            eventKey={8}
                            disabled={
                                isMaintenanceJob ||
                                isRepairJob ||
                                isWarrantyJob ||
                                isServicePlanJob ||
                                isCommissioningJob ||
                                anyVirtualIBs
                            }
                        >
                            {String(t('mobile:passAll'))}
                        </Dropdown.Item>
                        <Dropdown.Item
                            eventKey={9}
                            disabled={
                                isMaintenanceJob ||
                                isRepairJob ||
                                isWarrantyJob ||
                                isServicePlanJob ||
                                isCommissioningJob ||
                                anyVirtualIBs
                            }
                        >
                            {String(t('mobile:cannotAllActionButtonItem'))}
                        </Dropdown.Item>
                        <Dropdown.Item eventKey={20} disabled={selection.length === 0 || !isAnnualJob}>
                            {String(t('mobile:decommissionAssets'))}
                        </Dropdown.Item>
                        <Dropdown.Item eventKey={21} disabled={selection.length === 0 || !isAnnualJob}>
                            {String(t('mobile:markNotUnderContract'))}
                        </Dropdown.Item>

                        <Dropdown.Item eventKey={17} disabled={!enableUnlinkWOS(selection, installBasesPopulated)}>
                            {String(t('manageInventory:unlinkWorkOrders'))}
                        </Dropdown.Item>

                        <Dropdown.Item disabled={anyVirtualIBs} eventKey={10}>
                            {String(t('mobile:editSelection'))}
                        </Dropdown.Item>
                        <Dropdown.Item disabled={anyVirtualIBs || !canDeleteAssets()} eventKey={11}>
                            {String(t('mobile:deleteSelection'))}
                        </Dropdown.Item>
                        <Dropdown.Item disabled={anyVirtualIBs} eventKey={12}>
                            {String(t('mobile:editSelectionProduct'))}
                        </Dropdown.Item>
                        <Dropdown.Item eventKey={13}>{String(t('mobile:resetSelection'))}</Dropdown.Item>
                    </>
                )}
            </DropdownButton>
        </div>
    );
};
